import React, { useState } from 'react';
import { CheckIcon, MinusIcon } from '@heroicons/react/24/solid';
import { Disclosure } from '@headlessui/react';
import { MinusSmallIcon, PlusSmallIcon } from '@heroicons/react/24/outline';
import Background from '../ui/Background';
import CallToAction from './CallToAction';
import LandingHeader from '../ui/LandingHeader';
import LandingFooter from '../ui/LandingFooter';

export const pricingPageTitle = "Pricing";
export const pricingPageSubtitle = "Get started for free and upgrade as your needs grow. No credit card required.";

type FrequencyType = 'monthly' | 'annually';

interface Frequency {
    value: FrequencyType;
    label: string;
    priceSuffix: string;
}

const frequencies: Frequency[] = [
    { value: 'monthly', label: 'Monthly', priceSuffix: '/month' },
    { value: 'annually', label: 'Annually', priceSuffix: '/year' },
];

export const pricingTiers = [
    {
      name: "Free",
      id: "tier-free",
      href: "/sign-up",
      price: { monthly: "$0", annually: "$0" },
      description: "For side projects, experiments, and curious minds.",
      features: [
        "Up to 5 projects",
        "2GB storage",
        "Public templates",
        "Standard components",
        "Offline export support",
        "Community support",
        "Bring your own analytics"
      ],
      buttonText: "Get Started",
    },
    {
      name: "Pro",
      id: "tier-pro",
      href: "/sign-up",
      price: { monthly: "$59", annually: "$599" },
      description: "For serious builders who want privacy, power, and polish.",
      features: [
        "Everything in Free",
        "Up to 20 projects",
        "10GB storage",
        "Private templates",
        "Custom components",
        "Priority support",
        "Built-in analytics dashboard"
      ],
      buttonText: "Start Free Trial",
    },
    {
      name: "Team",
      id: "tier-teams",
      href: "/sign-up",
      price: { monthly: "$117", annually: "$1190" },
      description: "For teams that ship fast and want to stay organized doing it.",
      features: [
        "Everything in Pro",
        "Unlimited projects",
        "50GB storage",
        "API access",
        "Team workspaces",
        "Team analytics",
        "Custom branding",
        "SSO support",
        "Dedicated support"
      ],
      buttonText: "Start Trial",
    },
    {
      name: "Enterprise",
      id: "tier-enterprise",
      href: "/contact",
      price: "Custom",
      description: "For orgs that need control, compliance, and a red phone line.",
      features: [
        "Everything in Team",
        "Unlimited storage",
        "On-premise or private deployment",
        "Custom integrations & reviews",
        "Audit logs & data residency",
        "99.99% uptime SLA",
        "Dedicated success manager"
      ],
      buttonText: "Contact Sales",
    },
  ];

  
const faqs = [
    {
        question: "What counts as a deployment?",
        answer: "A deployment refers to each unique published app or environment on Webwald — e.g. staging, prod, or multiple versions."
    },
    {
        question: "Can I use Preswald for client projects?",
        answer: "Yes. You can build and deploy client-facing dashboards or tools under any paid plan. For multiple client workspaces, the Teams or Enterprise plan is recommended for isolation and management."
    },
    {
        question: "Can I cancel anytime?",
        answer: "Yes — you can downgrade or cancel your Preswald subscription at any time. Your local projects will continue to work."
    },
    {
        question: "Is there a student or OSS discount?",
        answer: "Yes. We offer discounts for educational use, nonprofits, and open-source projects. Contact us for eligibility."
    },
    {
        question: "What happens when I hit the limits of my plan?",
        answer: "You'll be notified and can either upgrade your plan or reduce usage. Paid plans offer significantly higher limits and capabilities."
    },
    {
        question: "Is Preswald usage-based or seat-based?",
        answer: "Preswald pricing is primarily feature-tier and usage-based, not seat-based. This means you're charged based on the capabilities and limits (like storage, number of projects, integrations), not per user. Teams and Enterprise plans support multiple users with no per-seat charges."
    }
];

const PricingPage: React.FC = () => {
    const [frequency, setFrequency] = useState<Frequency>(frequencies[0]);

    return (
        <Background>
            <div className="relative z-10">
                <LandingHeader />
            </div>
            <main className="container mx-auto px-4 py-6 sm:py-6 flex-grow mt-16">
                <div className="mx-auto max-w-3xl text-center">
                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
                        {pricingPageTitle}
                    </h1>
                    <p className="mt-4 text-muted-foreground text-base">
                        {pricingPageSubtitle}
                    </p>
                </div>

                <div className="mt-6 flex justify-center">
                    <fieldset aria-label="Payment frequency">
                        <div className="grid grid-cols-2 gap-x-1 rounded-full p-1 text-center text-xs font-semibold ring-1 ring-inset ring-border">
                            {frequencies.map((option) => (
                                <button
                                    key={option.value}
                                    onClick={() => setFrequency(option)}
                                    className={`cursor-pointer rounded-full px-2.5 py-1 text-muted-foreground ${
                                        frequency.value === option.value
                                            ? 'bg-primary text-primary-foreground'
                                            : 'hover:bg-muted'
                                    }`}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </fieldset>
                </div>

                <div className="mt-6">
                    <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-4">
                        {pricingTiers.map((tier) => (
                            <div
                                key={tier.id}
                                className="rounded-lg border bg-card text-card-foreground shadow-sm ring-1 ring-border flex flex-col"
                            >
                                <div className="p-4 sm:p-6 flex flex-col flex-grow">
                                    <h3 className="text-xl font-semibold tracking-tight">
                                        {tier.name}
                                    </h3>
                                    <p className="mt-2 text-sm text-muted-foreground">
                                        {tier.description}
                                    </p>
                                    <div className="mt-4 flex flex-col">
                                        <div className="flex items-baseline">
                                            <span className="text-3xl font-bold tracking-tight">
                                                {typeof tier.price === 'string' ? tier.price : tier.price[frequency.value]}
                                            </span>
                                            {typeof tier.price !== 'string' && tier.price[frequency.value] !== "Free Forever" && (
                                                <span className="ml-1 text-sm text-muted-foreground">
                                                    {frequency.priceSuffix}
                                                </span>
                                            )}
                                        </div>
                                        {typeof tier.price !== 'string' && tier.price[frequency.value] !== "Free Forever" && (
                                            <div className="mt-2 flex justify-start">
                                                <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium ${
                                                    frequency.value === 'annually' 
                                                        ? 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20' 
                                                        : 'bg-yellow-50 text-yellow-700 ring-1 ring-inset ring-yellow-600/20'
                                                }`}>
                                                    {frequency.value === 'annually' 
                                                        ? 'You\'re saving 15%' 
                                                        : 'Save 15% with annual billing'}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    <ul className="mt-4 space-y-2 flex-grow">
                                        {tier.features.map((feature) => (
                                            <li key={feature} className="flex items-center gap-2">
                                                <CheckIcon className="h-4 w-4 flex-shrink-0 text-primary" />
                                                <span className="text-sm text-muted-foreground">
                                                    {feature}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>

                                    <a
                                        href={tier.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-4 block w-full rounded-md bg-primary px-3 py-2 text-center text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                                    >
                                        {tier.buttonText}
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </main>

            {/* Price Comparison Table */}
            <section className="bg-white border border-[#e0e0e0]">
                <div className="max-w-7xl mx-auto px-4 py-12">
                    <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-left align-top">
                            <thead>
                                <tr>
                                    <th className="py-3 text-base font-semibold text-foreground">Feature</th>
                                    <th className="py-3 text-center font-semibold text-foreground">Free</th>
                                    <th className="py-3 text-center font-semibold text-foreground">Pro</th>
                                    <th className="py-3 text-center font-semibold text-foreground">Team</th>
                                    <th className="py-3 text-center font-semibold text-foreground">Enterprise</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {/* Core Features Section */}
                                <tr>
                                    <th colSpan={5} className="pt-8 pb-2 text-sm font-semibold text-foreground bg-background">Core Features</th>
                                </tr>
                                <tr><td className="py-3">Max Projects</td><td className="text-center">5</td><td className="text-center">20</td><td className="text-center">Unlimited</td><td className="text-center">Unlimited</td></tr>
                                <tr><td className="py-3">Storage</td><td className="text-center">2 GB</td><td className="text-center">10 GB</td><td className="text-center">50 GB</td><td className="text-center">Unlimited</td></tr>
                                <tr><td className="py-3">Templates (Public)</td>{[true,true,true,true].map((v,i)=>(<td key={i} className="text-center"><CheckIcon className="inline-block w-5 h-5 text-green-700" /></td>))}</tr>
                                <tr><td className="py-3">Templates (Private)</td>{[false,true,true,true].map((v,i)=>(<td key={i} className="text-center">{v?<CheckIcon className="inline-block w-5 h-5 text-green-700" />:<MinusIcon className="inline-block w-5 h-5 text-muted-foreground" />}</td>))}</tr>
                                <tr><td className="py-3">Custom Components</td>{[false,true,true,true].map((v,i)=>(<td key={i} className="text-center">{v?<CheckIcon className="inline-block w-5 h-5 text-green-700" />:<MinusIcon className="inline-block w-5 h-5 text-muted-foreground" />}</td>))}</tr>
                                <tr><td className="py-3">App Export (Offline)</td>{[true,true,true,true].map((v,i)=>(<td key={i} className="text-center"><CheckIcon className="inline-block w-5 h-5 text-green-700" /></td>))}</tr>
                                <tr><td className="py-3">Analytics</td><td className="text-center text-muted-foreground">BYO (manual setup)</td><td className="text-center">Built-in dashboards</td><td className="text-center">Team-level insights</td><td className="text-center">Custom org analytics</td></tr>
                                {/* Advanced Features Section */}
                                <tr>
                                    <th colSpan={5} className="pt-8 pb-2 text-sm font-semibold text-foreground bg-background">Advanced & Team</th>
                                </tr>
                                <tr><td className="py-3">API Access</td>{[false,false,true,true].map((v,i)=>(<td key={i} className="text-center">{v?<CheckIcon className="inline-block w-5 h-5 text-green-700" />:<MinusIcon className="inline-block w-5 h-5 text-muted-foreground" />}</td>))}</tr>
                                <tr><td className="py-3">Custom Integrations</td>{[false,false,true,true].map((v,i)=>(<td key={i} className="text-center">{v?<CheckIcon className="inline-block w-5 h-5 text-green-700" />:<MinusIcon className="inline-block w-5 h-5 text-muted-foreground" />}</td>))}</tr>
                                <tr><td className="py-3">Team Workspaces</td>{[false,false,true,true].map((v,i)=>(<td key={i} className="text-center">{v?<CheckIcon className="inline-block w-5 h-5 text-green-700" />:<MinusIcon className="inline-block w-5 h-5 text-muted-foreground" />}</td>))}</tr>
                                <tr><td className="py-3">SSO (Google, GitHub, etc.)</td>{[false,false,true,true].map((v,i)=>(<td key={i} className="text-center">{v?<CheckIcon className="inline-block w-5 h-5 text-green-700" />:<MinusIcon className="inline-block w-5 h-5 text-muted-foreground" />}</td>))}</tr>
                                <tr><td className="py-3">Branding & Theming</td>{[false,false,true,true].map((v,i)=>(<td key={i} className="text-center">{v?<CheckIcon className="inline-block w-5 h-5 text-green-700" />:<MinusIcon className="inline-block w-5 h-5 text-muted-foreground" />}</td>))}</tr>
                                <tr><td className="py-3">SLA / Uptime</td><td className="text-center text-muted-foreground">–</td><td className="text-center text-muted-foreground">–</td><td className="text-center">99.9%</td><td className="text-center">99.99%</td></tr>
                                <tr><td className="py-3">Hosting</td><td className="text-center">Shared</td><td className="text-center">Shared</td><td className="text-center">Shared or Dedicated</td><td className="text-center">On-Prem / Private Cloud</td></tr>
                                <tr><td className="py-3">Security Features</td><td className="text-center text-muted-foreground">–</td><td className="text-center text-muted-foreground">–</td><td className="text-center">Basic access controls</td><td className="text-center">Advanced, incl. audit logs</td></tr>
                                {/* Support Section */}
                                <tr>
                                    <th colSpan={5} className="pt-8 pb-2 text-sm font-semibold text-foreground bg-background">Support</th>
                                </tr>
                                <tr><td className="py-3">Support</td><td className="text-center">Community</td><td className="text-center">Priority Email</td><td className="text-center">Dedicated Slack/email</td><td className="text-center">CSM + Engineering Escalation</td></tr>
                            </tbody>
                        </table>
                    </div>
                    {/* Mobile stacked cards */}
                    <div className="md:hidden flex flex-col gap-4">
                        {/* Core Features Section */}
                        <div>
                            <div className="text-sm font-semibold text-foreground bg-background pt-4 pb-2">Core Features</div>
                            {[
                                { label: 'Max Projects', values: ['5', '20', 'Unlimited', 'Unlimited'] },
                                { label: 'Storage', values: ['2 GB', '10 GB', '50 GB', 'Unlimited'] },
                                { label: 'Templates (Public)', values: [true, true, true, true] },
                                { label: 'Templates (Private)', values: [false, true, true, true] },
                                { label: 'Custom Components', values: [false, true, true, true] },
                                { label: 'App Export (Offline)', values: [true, true, true, true] },
                                { label: 'Analytics', values: ['BYO (manual setup)', 'Built-in dashboards', 'Team-level insights', 'Custom org analytics'] },
                            ].map((row, idx) => (
                                <div key={idx} className="flex flex-col border-b border-border py-2">
                                    <div className="font-medium text-foreground mb-1">{row.label}</div>
                                    <div className="grid grid-cols-4 gap-2">
                                        {row.values.map((v, i) => (
                                            <div key={i} className="text-center">
                                                {typeof v === 'boolean'
                                                    ? v
                                                        ? <CheckIcon className="inline-block w-5 h-5 text-green-700" />
                                                        : <MinusIcon className="inline-block w-5 h-5 text-muted-foreground" />
                                                    : <span className={i === 0 ? 'text-foreground' : ''}>{v}</span>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                        {/* Advanced & Team Section */}
                        <div>
                            <div className="text-sm font-semibold text-foreground bg-background pt-4 pb-2">Advanced & Team</div>
                            {[
                                { label: 'API Access', values: [false, false, true, true] },
                                { label: 'Custom Integrations', values: [false, false, true, true] },
                                { label: 'Team Workspaces', values: [false, false, true, true] },
                                { label: 'SSO (Google, GitHub, etc.)', values: [false, false, true, true] },
                                { label: 'Branding & Theming', values: [false, false, true, true] },
                                { label: 'SLA / Uptime', values: ['–', '–', '99.9%', '99.99%'] },
                                { label: 'Hosting', values: ['Shared', 'Shared', 'Shared or Dedicated', 'On-Prem / Private Cloud'] },
                                { label: 'Security Features', values: ['–', '–', 'Basic access controls', 'Advanced, incl. audit logs'] },
                            ].map((row, idx) => (
                                <div key={idx} className="flex flex-col border-b border-border py-2">
                                    <div className="font-medium text-foreground mb-1">{row.label}</div>
                                    <div className="grid grid-cols-4 gap-2">
                                        {row.values.map((v, i) => (
                                            <div key={i} className="text-center">
                                                {typeof v === 'boolean'
                                                    ? v
                                                        ? <CheckIcon className="inline-block w-5 h-5 text-green-700" />
                                                        : <MinusIcon className="inline-block w-5 h-5 text-muted-foreground" />
                                                    : <span className={i === 0 ? 'text-foreground' : ''}>{v}</span>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                        {/* Support Section */}
                        <div>
                            <div className="text-sm font-semibold text-foreground bg-background pt-4 pb-2">Support</div>
                            {[
                                { label: 'Support', values: ['Community', 'Priority Email', 'Dedicated Slack/email', 'CSM + Engineering Escalation'] },
                            ].map((row, idx) => (
                                <div key={idx} className="flex flex-col border-b border-border py-2">
                                    <div className="font-medium text-foreground mb-1">{row.label}</div>
                                    <div className="grid grid-cols-4 gap-2">
                                        {row.values.map((v, i) => (
                                            <div key={i} className="text-center">
                                                <span className={i === 0 ? 'text-foreground' : ''}>{v}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <CallToAction />

            <div className="bg-white border border-[#e0e0e0]">
                <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8 lg:py-40">
                    <div className="mx-auto max-w-4xl">
                        <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-6">
                            FAQ
                        </h3>
                        <dl className="mt-8 divide-y divide-border">
                            {faqs.map((faq) => (
                                <Disclosure key={faq.question} as="div" className="py-6 first:pt-0 last:pb-0">
                                    <dt>
                                        <Disclosure.Button className="group flex w-full items-start justify-between text-left text-foreground">
                                            <span className="text-base font-semibold leading-7">{faq.question}</span>
                                            <span className="ml-6 flex h-7 items-center">
                                                <PlusSmallIcon aria-hidden="true" className="size-6 group-data-[open]:hidden" />
                                                <MinusSmallIcon aria-hidden="true" className="size-6 group-[&:not([data-open])]:hidden" />
                                            </span>
                                        </Disclosure.Button>
                                    </dt>
                                    <Disclosure.Panel as="dd" className="mt-2 pr-12">
                                        <p className="text-base text-muted-foreground leading-7">{faq.answer}</p>
                                    </Disclosure.Panel>
                                </Disclosure>
                            ))}
                        </dl>
                    </div>
                </div>
            </div>
            
            <LandingFooter />
        </Background>
    );
};

export default PricingPage; 